import React from 'react';
import Footer from './Footer';

interface PrivacyPageProps {
  darkMode: boolean;
}

const PrivacyPage: React.FC<PrivacyPageProps> = ({ darkMode }) => {
  const theme = {
    bg: darkMode ? 'bg-[#1e1e1e]' : 'bg-gray-100',
    panelBg: darkMode ? 'bg-[#252526]' : 'bg-white',
    text: darkMode ? 'text-[#d4d4d4]' : 'text-gray-900',
    mutedText: darkMode ? 'text-[#858585]' : 'text-gray-500',
    label: darkMode ? 'text-[#bbbbbb]' : 'text-gray-700',
    border: darkMode ? 'border-[#3e3e42]' : 'border-gray-200',
    link: darkMode ? 'text-[#4a9eff] hover:text-[#6bb5ff]' : 'text-blue-600 hover:text-blue-700',
  };

  return (
    <div className={`min-h-screen ${theme.bg} ${theme.text} py-12 px-4`}>
      <div className={`max-w-4xl mx-auto ${theme.panelBg} rounded-lg shadow-lg p-8 ${theme.border} border`}>
        <h1 className={`text-3xl font-bold mb-6 ${theme.text}`}>Privacy Policy</h1>
        <p className={`text-sm mb-8 ${theme.mutedText}`}>Last Updated: Dec 5, 2025</p>

        <div className="prose prose-invert max-w-none space-y-6">
          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>1. Scope of This Policy</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              This Privacy Policy explains how we handle information for the JSON Formatter tool available at 
              <strong className="font-semibold"> https://jsonformatter.echologai.com</strong>. This tool is part of the EchoLog ecosystem.
            </p>
            <p className={`mb-4 ${theme.text} leading-7`}>
              By using this tool, you agree to the practices described in this Privacy Policy. For comprehensive privacy information 
              about all EchoLog services, please see our{' '}
              <a href="https://echologai.com/privacy/" target="_blank" rel="noopener noreferrer" className={`${theme.link} underline`}>
                main Privacy Policy
              </a>.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>2. Client-Side Processing</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              The JSON Formatter tool is designed with privacy as a core principle. All JSON processing happens 
              <strong className="font-semibold"> entirely in your browser</strong> using JavaScript. This means:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${theme.text} leading-7`}>
              <li>Your JSON data is <strong>never sent to our servers</strong> or any external API</li>
              <li>All formatting, validation, and parsing occurs locally on your device</li>
              <li>We do not store, log, or retain your JSON content</li>
              <li>Your data remains completely private and secure</li>
            </ul>
            <p className={`mb-4 ${theme.text} leading-7`}>
              This client-side approach makes the tool safe for working with sensitive data, including API keys, 
              configuration files, user data, or logs from production systems.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>3. Local Storage</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              The tool may use browser local storage to:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${theme.text} leading-7`}>
              <li>Cache your recent JSON input for convenience (auto-save feature)</li>
              <li>Remember your dark mode preference</li>
              <li>Store other user preferences</li>
            </ul>
            <p className={`mb-4 ${theme.text} leading-7`}>
              This data stays on your device and is never transmitted to us. You can clear this data at any time 
              through your browser settings.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>4. Advertising (Google AdSense)</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              This website displays advertisements served by Google AdSense and its partners. These ads help us 
              provide the tool free of charge.
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${theme.text} leading-7`}>
              <li>Third-party vendors, including Google, use cookies to serve ads based on your visits to this site and other websites</li>
              <li>Google's use of advertising cookies enables it and its partners to serve ads to you based on your browsing activity</li>
              <li>You may opt out of personalized advertising by visiting{' '}
                <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className={`${theme.link} underline`}>
                  Google's Ads Settings
                </a>
              </li>
            </ul>
            <p className={`mb-4 ${theme.text} leading-7`}>
              We do not have access to or control over the cookies used by third-party advertisers. Their activities 
              are governed by their own privacy policies.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>5. Cookies and Tracking</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              We use cookies and similar technologies to:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${theme.text} leading-7`}>
              <li>Remember your preferences (dark mode, etc.)</li>
              <li>Support advertising as described above</li>
              <li>Improve the functionality and user experience</li>
            </ul>
            <p className={`mb-4 ${theme.text} leading-7`}>
              You can control or disable cookies through your browser settings. However, disabling cookies may affect 
              the functionality of certain features.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>6. Data Sharing</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              We do not sell your personal information. We may share limited data only in the following situations:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${theme.text} leading-7`}>
              <li>With service providers (such as advertising networks) solely to operate and provide the service</li>
              <li>When required by law, regulation, or legal process</li>
              <li>To protect the rights, property, or safety of EchoLog, our users, or the public</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>7. Children's Privacy</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              This tool is not directed to children under the age of 13, and we do not knowingly collect personal 
              information from children under 13. If you believe that a child has provided us with personal information, 
              please contact us so that we can take appropriate action.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>8. Updates to This Policy</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, 
              or legal requirements. When we do, we will update the "Last Updated" date at the top of this page. 
              We encourage you to review this Policy periodically.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>9. Contact Us</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              If you have any questions or concerns regarding this Privacy Policy or our data practices, please contact us at{' '}
              <a href="mailto:echolog07@gmail.com" className={`${theme.link} underline`}>echolog07@gmail.com</a>.
            </p>
            <p className={`mb-4 ${theme.text} leading-7`}>
              For more comprehensive privacy information about all EchoLog services, please visit our{' '}
              <a href="https://echologai.com/privacy/" target="_blank" rel="noopener noreferrer" className={`${theme.link} underline`}>
                main Privacy Policy
              </a>.
            </p>
          </section>
        </div>

        <div className={`mt-8 pt-6 border-t ${theme.border}`}>
          <a 
            href="/" 
            className={`inline-flex items-center ${theme.link} hover:underline`}
          >
            ← Back to JSON Formatter
          </a>
        </div>
      </div>
      <Footer darkMode={darkMode} />
    </div>
  );
};

export default PrivacyPage;

